import React, { FC, useEffect, useState, FormEvent } from "react";
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { clearState, login, sendOtp } from "../../store/authSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import countries from "../../assets/countries.json";

// material-ui
import {
  Checkbox,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  Link,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
  TextField,
  Autocomplete,
  Box,
} from "@mui/material";

// third party
import * as Yup from "yup";
import { Formik } from "formik";

// assets
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import SocialIcons from "../../components/SocialIcons/SocialIcons";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import passwordSlice from "../../store/passwordSlice";

import './css2/style.css';

interface LoginProps {
  setType: (k: any) => void;
}

const SigninForm: FC<LoginProps> = ({ setType }) => {
  const [checked, setChecked] = React.useState(false);
  const { isLoggedIn, isError, errorMessage } = useAppSelector(
    (state) => state.auth
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const [isemail, setIsEmail] = useState(true);
  const [isotp, setIsOtp] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [countryCode, setCountryCode] = useState({
    code: "IN",
    dial_code: "+91",
    name: "India"
  });
  const [showCode, setShowCode] = useState(false);
  const [textValue, setTextValue] = useState('')
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
  };

  const handleOnChange = (event: FormEvent) => {
    let currentHtmlElement = (event.target as HTMLInputElement);
    if (currentHtmlElement.name === 'email') {
      let currentValue = currentHtmlElement.value;
      if (currentValue) setTextValue(currentValue);
      if (isNaN(parseInt(currentValue))) {
        setShowCode(false);
      } else {
        setShowCode(true);
      }
    }
  };

  // Update UI based on the state(Success or Error)
  useEffect(() => {
    let token: any = searchParams.get("inviteToken");

    if (isError) {
      dispatch(clearState());
    }
    if (isLoggedIn) {
      dispatch(clearState());
      let navigateObj: any = { path: "/home" };
      if (token) {
        navigateObj["search"] = "?inviteToken=" + token;
        navigate("/home?inviteToken=" + token, { replace: true });
      } else {
        navigate("/home", { replace: true });
      }
      // navigate('/home', { replace: true });
      // navigate(navigateObj, { replace: true });
    }
  }, [isError, errorMessage, isLoggedIn, dispatch, navigate]);

  const [seconds, setSeconds] = useState(0);
  var timer: any;

  useEffect(() => {
    timer = seconds > 0 && setTimeout(() => setSeconds(seconds - 1), 1000);
    return () => clearInterval(timer);
  });

  const resend = (email: any) => {

    if (showCode) {
      dispatch(sendOtp({ email: countryCode.dial_code + email }));
    } else {
      dispatch(sendOtp({ email }));
    }

    setSeconds(60);
  };

  const validateEmail = (email: any) => { //Validates the email address
    var emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(email);
  }

  return (
    <>
      <Formik
        initialValues={{
          email: "",
          password: "",
          otp: "",
          agreeTerms: false,
          submit: null,
        }}
        validationSchema={Yup.object().shape(
          {
            email: Yup.string().when("isEmail", {
              is: "1",
              then: Yup.string()
                .email("Please enter a valid email address")
                .required("Email address cannot be empty"),
              otherwise: Yup.string()
                .required("Email address or mobile phone number")
                .min(6, "Invalid phone number or email address"),
            })
            , 'password': Yup.string()
              .when('otp', {
                is: (otp: any) => !otp || otp.length === 0,
                then: Yup.string()
                  .required('Password is required')
              })
            , 'otp': Yup.string()
              .when('password', {
                is: (password: any) => !password || password.length === 0,
                then: Yup.string()
                  .required('OTP is required')
              })
            , 'agreeTerms': Yup.boolean()
              .oneOf([true], 'You must agree to the Terms of Service and Privacy Policy')
          }, [['otp', 'password']])}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          try {
            setStatus({ success: false });
            setSubmitting(true);
            let { email, password, otp } = values;
            if (!validateEmail(email)) {
              email = countryCode.dial_code + email;
            }
            const loginReponse = await dispatch(login({ email, password, otp }));
            console.log('loginReponse: ', loginReponse);

            // Only reload if login was successful
            if (loginReponse.type === 'auth/login/fulfilled') {
              window.location.reload();
            } else {
              setSubmitting(false);
              // Handle rejected login
              if (loginReponse.payload && 'message' in loginReponse.payload) {
                setErrors({ submit: loginReponse.payload.message });
              }
            }
          } catch (err: any) {
            setStatus({ success: false });
            setErrors({ submit: err.message });
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleReset,
          handleSubmit,
          isSubmitting,
          touched,
          values,
          setFieldTouched,
          validateField,
        }) => (
          <form noValidate onSubmit={handleSubmit} onChange={handleOnChange} style={{ paddingLeft: '35px' }}>

            <Typography style={{ float: 'right', marginTop: 10 }} component={Link} onClick={() => setType('1')} variant="body1" sx={{ textDecoration: 'none' }} color="#aaa">
              Don&apos;t have an account?
            </Typography>
            <h3 className="mb-5">Login</h3>
            {isemail ? (
              <>
                {showCode && <div className="form-wrapper">
                  <Autocomplete
                    id="country-select-demo"
                    options={countries}
                    autoHighlight
                    value={countryCode}
                    getOptionLabel={(option) => option.dial_code}
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                        {...props}
                      >
                        <img
                          loading="lazy"
                          width="20"
                          src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                          srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                          alt=""
                        />
                        {option.dial_code}
                      </Box>
                    )}
                    onChange={(event, newValue) => {
                      if (newValue) setCountryCode(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Country"
                        variant="standard"
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: "new-password", // disable autocomplete and autofill
                        }}
                      />
                    )}
                  />
                </div>
                }
                <label>Email address or mobile phone number</label>
                <TextField
                  required
                  className='c-text'
                  variant="standard"
                  sx={{ width: '100%', mt: 0.5, mb: 2 }}
                  InputProps={{
                    disableUnderline: true,
                  }}
                  size='small'
                  id="email-login"
                  type="text"
                  value={values.email}
                  name="email"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder="Email / Phone"
                  fullWidth
                  error={Boolean(touched.email && errors.email)}
                />
                {touched.email && errors.email && (
                  <FormHelperText
                    error
                    id="standard-weight-helper-text-email-login"
                  >
                    {errors.email}
                  </FormHelperText>
                )}
                {/* <Button
                      fullWidth
                      size="large"
                      variant="contained"
                      color="primary"
                      
                    >
                      Next
                    </Button> */}
                <button className="c-button" style={{ width: '60%' }} type="button" onClick={async () => {
                  setFieldTouched('email', true);
                  await validateField('email');
                  if (!errors.email && textValue) {
                    setIsEmail(false);
                    values.password = "";
                  }
                }}>Next</button>
              </>
            ) : (
              <>
                <>
                  <Grid item xs={12} sx={{ display: 'flex' }}>
                    <ArrowBackIosIcon
                      fontSize="small"
                      onClick={() => setIsEmail(true)}
                      cursor="pointer"
                    />
                    <Typography>{values.email}</Typography>
                  </Grid>
                  <Grid item xs={12} sx={{ paddingTop: '10px' }}>
                    {!isotp ? (
                      <>
                        <label className="mt-2">Enter OTP</label>
                        <TextField
                          fullWidth
                          className='c-text'
                          variant="standard"
                          sx={{ width: '100%', mt: 0.5 }}
                          size='small'
                          error={Boolean(touched.otp && errors.otp && values.submit !== null)}
                          id="otp"
                          name="otp"
                          type="text"
                          value={values.otp}
                          placeholder="Enter OTP"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          InputProps={{
                            disableUnderline: true,
                          }}
                        />
                        {touched.otp && errors.otp && values.submit !== null && (
                          <FormHelperText
                            error
                            id="standard-weight-helper-text-otp-login"
                          >
                            {errors.otp}
                          </FormHelperText>
                        )}
                        {seconds > 0 ? (
                          <Typography>
                            Resend OTP In : {seconds}
                          </Typography>
                        ) : (
                          <Box
                            onClick={() => resend(values.email)}
                            sx={{ cursor: "pointer" }}
                          >
                            <Link>Resend OTP</Link>
                          </Box>
                        )}
                      </>
                    ) : (
                      <>
                        <label className="mt-2">Password</label>
                        <TextField
                          fullWidth
                          className='c-text'
                          variant="standard"
                          sx={{ width: '100%', mt: 0.5 }}
                          size='small'
                          error={Boolean(
                            touched.password && errors.password
                          )}
                          id="-password-login"
                          type={showPassword ? "text" : "password"}
                          value={values.password}
                          name="password"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          InputProps={{
                            disableUnderline: true,
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton style={{ marginRight: -8, marginTop: -2 }}
                                  aria-label="toggle password visibility"
                                  onClick={handleClickShowPassword}
                                  onMouseDown={handleMouseDownPassword}
                                  edge="end"
                                  size="large"
                                >
                                  {showPassword ? (
                                    <EyeOutlined style={{ fontSize: 14 }} />
                                  ) : (
                                    <EyeInvisibleOutlined style={{ fontSize: 14 }} />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          placeholder="Enter password"
                        />
                        {touched.password && errors.password && (
                          <FormHelperText
                            error
                            id="standard-weight-helper-text-password-login"
                          >
                            {errors.password}
                          </FormHelperText>
                        )}
                      </>
                    )}
                  </Grid>
                  <Grid item xs={12} sx={{ mt: -1 }}>

                    {!isotp ? (
                      <Typography
                        variant="body2"
                        sx={{ cursor: "pointer", color: 'primary.main', fontWeight: 600, mt: 2, mb: 1 }}
                        onClick={() => { setIsOtp(true); clearInterval(timer); }}
                      >
                        Login with Password
                      </Typography>
                    ) : (
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2} mb={1}>
                        <Typography
                          variant="body2"
                          sx={{ cursor: "pointer", color: 'primary.main', fontWeight: 600 }}
                          onClick={() => { values.password = ''; setIsOtp(false); resend(values.email); }}
                        >
                          Login with OTP
                        </Typography>
                        <Link
                          variant="body2"
                          component={Link}
                          onClick={() => setType('2')}
                          color="text.primary"
                          sx={{ cursor: "pointer", fontWeight: 600 }}
                        >
                          Forgot password?
                        </Link>
                      </Stack>
                    )}
                    {/* <FormControlLabel
                      
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={(event) =>
                            setChecked(event.target.checked)
                          }
                          name="checked"
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="h6">
                          Keep me sign in
                        </Typography>
                      }
                    /> */}
                  </Grid>
                </>
                <FormControlLabel
                  sx={{ mt: 0.5, mb: 0.5 }}
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      name="agreeTerms"
                      checked={values.agreeTerms}
                      onChange={handleChange}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      By Signing up, you agree to our &nbsp;
                      <Link variant="subtitle2" component={RouterLink} to="/terms-of-use" color='primary.light'>
                        Terms of Service
                      </Link>
                      &nbsp; and &nbsp;
                      <Link variant="subtitle2" component={RouterLink} to="/privacy-policy" color='primary.light'>
                        Privacy Policy
                      </Link>
                    </Typography>
                  }
                />
                {touched.agreeTerms && errors.agreeTerms && (
                  <FormHelperText error>{errors.agreeTerms}</FormHelperText>
                )}
                <>
                  {errors.submit && (
                    <Grid item xs={12}>
                      <FormHelperText error>{errors.submit}</FormHelperText>
                    </Grid>
                  )}
                </>
                <Grid item xs={12} mt={1.5}>
                  <button
                    className="c-button"
                    disabled={isSubmitting}
                    style={{ width: '60%' }}
                    type="submit"
                  >
                    Login
                  </button>
                </Grid>
              </>
            )}
            <Grid item xs={12} mt={3} mb={3}>
              <Divider>
                <Typography variant="caption"> Login with</Typography>
              </Divider>
            </Grid>
            <Grid item xs={12}>
              <SocialIcons />
            </Grid>
          </form>
        )}
      </Formik>
    </>
  );
};

export default SigninForm;
